import { ReactElement, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Component as PCLogo } from 'consumer/components/logos/pclogo-black.svg';
import { Component as SearchIcon } from 'icons/icon-ui--search.svg';
import { HeaderSearch } from './HeaderSearch';
import { Route } from '../../utils/config';
import { useMenuWithCart } from '../../contexts/MenuWithCart';
import CartDrawerContainer from '../cart-drawer/CartDrawerContainer';
import { HeaderSidebar } from './HeaderSidebar';
import SignInModal from '../sign-in/SignInModal';
import { Button } from '../shared/button/Button';
import Link from '../shared/link/Link';
import { SignupStep } from '../../constants/signup';
import styles from './Header.module.scss';
import { getFormattedDeliveryDateRange } from '../../utils/dateTimes';
import { formatDeliveryDate } from '../../hooks/useFormattedDate';
import { useLoggedInMenuIdQuery } from '../../services/menu/menu.queries';

type Props = {
  withCart?: boolean;
}

export function LoggedOutHeader({ withCart = false }: Props): ReactElement {
  const { menu } = useMenuWithCart();
  const shippingOnDate = menu?.deliveryOn ?? '';
  const formattedEstimatedDeliveryDate = getFormattedDeliveryDateRange(shippingOnDate, false);
  const { currentSignupStep } = useMenuWithCart();
  const navigate = useNavigate();
  const [signinModalVisible, setSignInModalVisible] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const isPaying = currentSignupStep === SignupStep.PAYMENT;
  const menuId = menu?.id;
  const loggedinMenu = useLoggedInMenuIdQuery(menuId, isPaying).data;
  const deliveryOn = isPaying ? loggedinMenu?.deliveryOn : menu?.deliveryOn;
  const deliveryDateFormatted = useMemo(() => formatDeliveryDate(deliveryOn), [deliveryOn]);

  return (
    <header role="banner" className={styles.header}>
      <div className={`${styles.header_content} ${styles.logged_out}`}>
        <div className={styles.sidebar_and_search}>
          <HeaderSidebar setSignInModalVisible={setSignInModalVisible} />
          <button
            type="button"
            className={styles.mobile_search_icon}
            aria-label="Search"
            onClick={() => setMobileSearchOpen((o) => !o)}
          >
            <SearchIcon width={20} height={20} />
          </button>
        </div>

        <div className={styles.logo_and_search}>
          <a
            title="Purple Carrot"
            className={styles.pc_logo}
            data-cy="pc-logo"
            aria-label="Home"
            href="/shop"
            onClick={(e) => { e.preventDefault(); navigate('/shop'); }}
            tabIndex={0}
          >
            <PCLogo width="135" focusable="false" />
          </a>
        </div>

        <div className={styles.header_search_center}>
          <HeaderSearch />
        </div>
        <nav aria-label="Logged in site navigation">
          <ul className={styles.nav_container}>

            <div className={styles.shop_link}>
              <li>
                <Link
                  href="/shop"
                  data-heap-id="header-shop"
                  className={({ isActive }) => (`${styles.nav_item} ${isActive ? styles.is_active : ''}`)}
                >
                  Shop
                  {shippingOnDate !== '' && (
                    <>
                      {' '}
                      |
                      {' '}
                      {isPaying ? deliveryDateFormatted : formattedEstimatedDeliveryDate}
                    </>
                  )}
                </Link>
              </li>
            </div>

            <div className={styles.nav_links}>
              {/* TODO: add list item for "Find My Meals" */}
              <li>
                <Link
                  href={Route.WHY_PLANTS}
                  data-heap-id="header-why-plants"
                  className={({ isActive }) => (`${styles.nav_item} ${isActive ? styles.is_active : ''}`)}
                  reload
                >
                  Why Plants
                </Link>
              </li>
            </div>

            <li>
              {currentSignupStep && currentSignupStep !== SignupStep.CREATE_ACCOUNT ? (
                <form
                  method="POST"
                  action="/users/sign_out"
                  onSubmit={() => {
                    document.cookie = `account_creation_email=; path=/; expires=${new Date(
                      0,
                    ).toUTCString()}`;
                  }}
                >
                  <input type="hidden" name="_method" value="delete" />
                  <input
                    className={`${styles.login_button} ${styles.nav_item}`}
                    type="submit"
                    value="Log Out"
                  />
                </form>
              ) : (
                <Button
                  onClick={() => {
                    setSignInModalVisible(!signinModalVisible);
                  }}
                  data-heap-id="header-login"
                  className={`${styles.login_button} ${styles.nav_item}`}
                  text="Log In"
                />
              )}
            </li>

            {withCart && (
              <li className={styles.nav_item}>
                <CartDrawerContainer setSignInModalVisible={setSignInModalVisible} />
              </li>
            )}
          </ul>
        </nav>

        <SignInModal
          onClose={() => setSignInModalVisible(false)}
          visible={signinModalVisible}
        />
      </div>

      {mobileSearchOpen && (
        <div className={styles.mobile_search_dropdown}>
          <HeaderSearch autoFocus onBlur={() => setMobileSearchOpen(false)} />
        </div>
      )}
    </header>
  );
}
